import React, { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "./ui/command";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { toast } from "sonner";

// Searchable contact select with inline "add new". Searches name/email/phone.
export function ContactCombobox({ value, onValueChange, type = "customer", placeholder = "Select contact…", testId = "contact-combobox" }) {
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/contacts?type=${type}`);
      setContacts(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);

  const selected = contacts.find((c) => c.id === value);
  const q = search.trim().toLowerCase();
  const filtered = q
    ? contacts.filter((c) => [c.name, c.email, c.phone].some((v) => (v || "").toLowerCase().includes(q)))
    : contacts;

  const addNew = async () => {
    const name = search.trim();
    if (!name) return;
    setAdding(true);
    try {
      const { data } = await api.post("/contacts", { name, type, email: "", phone: "", address: "", bank_details: "" });
      await load();
      onValueChange(data.id, data.name);
      toast.success(`Added ${data.name}`);
      setSearch(""); setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox"
          className="w-full justify-between rounded-none mt-1 font-normal" data-testid={`${testId}-trigger`}>
          <span className={selected ? "" : "text-muted-foreground"}>{selected ? selected.name : placeholder}</span>
          <ChevronsUpDown className="w-4 h-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 rounded-none w-[--radix-popover-trigger-width]" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search name, email, phone…" value={search} onValueChange={setSearch} data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>
              <button type="button" onClick={addNew} disabled={adding || !search.trim()}
                className="flex items-center gap-2 w-full px-2 py-2 text-sm hover:bg-secondary disabled:opacity-50" data-testid={`${testId}-add`}>
                <Plus className="w-4 h-4" /> Add "{search.trim()}"
              </button>
            </CommandEmpty>
            <CommandGroup>
              {filtered.map((c) => (
                <CommandItem key={c.id} value={c.id} onSelect={() => { onValueChange(c.id, c.name); setOpen(false); }} data-testid={`${testId}-option`}>
                  <Check className={`mr-2 h-4 w-4 ${value === c.id ? "opacity-100" : "opacity-0"}`} />
                  <div className="flex flex-col">
                    <span>{c.name}</span>
                    {(c.email || c.phone) && <span className="text-xs text-muted-foreground">{c.email || c.phone}</span>}
                  </div>
                </CommandItem>
              ))}
              {search.trim() && (
                <CommandItem value={`__add__${search}`} onSelect={addNew} data-testid={`${testId}-add-inline`}>
                  <Plus className="mr-2 h-4 w-4" /> Add "{search.trim()}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
